import { useEffect, useState } from 'react';

/**
 * Tiny admin-only keyboard-shortcut layer. Press `?` to open a help overlay
 * listing every shortcut. Shortcuts are disabled when the user is typing in
 * an input, textarea, or contenteditable element.
 *
 * @param {Object} handlers map of single-character keys (lowercase) → handler fn
 */
export const useAdminShortcuts = (handlers) => {
    const [helpOpen, setHelpOpen] = useState(false);

    useEffect(() => {
        const onKey = (e) => {
            // Ignore modifier combos so we don't fight browser shortcuts
            if (e.ctrlKey || e.metaKey || e.altKey) return;

            // Ignore typing context (inputs, textareas, contentEditable, shadcn select etc)
            const t = e.target;
            if (!t) return;
            const tag = (t.tagName || '').toLowerCase();
            if (tag === 'input' || tag === 'textarea' || tag === 'select' || t.isContentEditable) return;

            // `?` (shift + /) opens help; Esc closes
            if (e.key === '?') {
                e.preventDefault();
                setHelpOpen((v) => !v);
                return;
            }
            if (e.key === 'Escape' && helpOpen) {
                setHelpOpen(false);
                return;
            }

            const key = e.key.toLowerCase();
            const fn = handlers[key];
            if (fn) {
                e.preventDefault();
                fn();
                setHelpOpen(false);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [handlers, helpOpen]);

    return { helpOpen, setHelpOpen };
};
